import { useCallback, useState } from 'react';
import { ErrorNote, Pager, Panel } from '../components/ui.js';
import { fmtWhen } from '../lib/format.js';
import { clearDismissedAlerts, dismissAlerts, getAdminLogs } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

const LEVEL_TEXT: Record<string, string> = {
  info: 'text-neutral-600',
  warn: 'text-amber-600',
  error: 'text-rose-600',
};
const LEVEL_DOT: Record<string, string> = {
  info: 'bg-neutral-300',
  warn: 'bg-amber-400',
  error: 'bg-rose-500',
};

const LIMIT = 40;

export function Logs() {
  // Events are paginated server-side (they grow fast); alerts come back full.
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const { data, error, reload } = useAsyncData(
    useCallback(() => getAdminLogs({ limit: LIMIT, offset }), [offset]),
  );

  const dismiss = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await dismissAlerts(ids);
      reload();
    } finally {
      setBusy(false);
    }
  };
  const restore = async () => {
    setBusy(true);
    try {
      await clearDismissedAlerts();
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Panel title="Alerts">
        {error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : !data ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : data.alerts.length === 0 ? (
          <div className="flex items-center justify-between gap-3">
            <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              ✓ No active alerts{(data.dismissedCount ?? 0) > 0 ? ' (some are cleared).' : '.'}
            </p>
            {(data.dismissedCount ?? 0) > 0 && (
              <button
                type="button"
                className="shrink-0 text-xs text-neutral-500 hover:text-neutral-800 hover:underline"
                disabled={busy}
                onClick={restore}
              >
                Restore {data.dismissedCount} cleared
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between text-xs text-neutral-400">
              <span>{data.alerts.length} active</span>
              <span className="flex items-center gap-3">
                {(data.dismissedCount ?? 0) > 0 && (
                  <button
                    type="button"
                    className="hover:text-neutral-700 hover:underline"
                    disabled={busy}
                    onClick={restore}
                  >
                    Restore {data.dismissedCount} cleared
                  </button>
                )}
                <button
                  type="button"
                  className="hover:text-neutral-700 hover:underline"
                  disabled={busy}
                  onClick={() => dismiss(data.alerts.map((a) => a.id))}
                >
                  Clear all
                </button>
              </span>
            </div>
            <ul className="space-y-2">
              {data.alerts.map((a) => (
                <li
                  key={a.id}
                  className={`rounded-lg border p-3 ${
                    a.severity === 'critical'
                      ? 'border-rose-200 bg-rose-50'
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold">{a.title}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-neutral-400">{fmtWhen(a.ts)}</span>
                      <button
                        type="button"
                        title="Clear this alert"
                        className="rounded px-1.5 text-sm text-neutral-400 hover:bg-white/60 hover:text-neutral-700"
                        disabled={busy}
                        onClick={() => dismiss([a.id])}
                      >
                        ✕
                      </button>
                    </span>
                  </div>
                  <p className="mt-0.5 break-words text-sm text-neutral-600">{a.detail}</p>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>

      <Panel title="Application logs">
        <div className="mb-2 flex items-center justify-between text-xs text-neutral-400">
          <span>Ingest jobs, webhook deliveries + inbound replies, newest first.</span>
          <button type="button" className="hover:text-neutral-700 hover:underline" onClick={reload}>
            Refresh
          </button>
        </div>
        {data &&
          (data.events.length === 0 ? (
            <p className="text-sm text-neutral-500">No activity yet.</p>
          ) : (
            <ul className="divide-y divide-neutral-100 font-mono text-xs">
              {data.events.map((e) => (
                <li key={e.id} className="flex items-start gap-2 py-1.5">
                  <span
                    className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${LEVEL_DOT[e.level] ?? LEVEL_DOT.info}`}
                  />
                  <span className="w-24 shrink-0 text-neutral-400">{fmtWhen(e.ts)}</span>
                  <span className="w-16 shrink-0 uppercase text-neutral-400">{e.source}</span>
                  <span className="min-w-0 flex-1">
                    <span className={LEVEL_TEXT[e.level] ?? LEVEL_TEXT.info}>{e.message}</span>
                    {e.detail && (
                      <span className="block truncate text-neutral-400">{e.detail}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ))}
        {data && (
          <Pager
            offset={offset}
            count={data.events.length}
            hasMore={data.pagination?.hasMore ?? false}
            onPrev={() => setOffset((o) => Math.max(0, o - LIMIT))}
            onNext={() => setOffset((o) => o + LIMIT)}
          />
        )}
      </Panel>
    </div>
  );
}
